import React, { useEffect, useState, lazy, Suspense } from 'react';
import { BookOpen, BarChart, Check, ArrowRight, Zap, ShieldCheck, Feather, ChevronDown } from 'lucide-react';
import { useTranslation, Trans } from 'react-i18next';
import { Capacitor } from '@capacitor/core';
import { getCurrencySymbol } from '../services/currencyService';

// Lazy load below-the-fold components (not visible on initial render)
const DisclaimerModal = lazy(() => import('./DisclaimerModal').then(m => ({ default: m.DisclaimerModal })));
const ContactModal = lazy(() => import('./ContactModal').then(m => ({ default: m.ContactModal })));
const BlogSection = lazy(() => import('./BlogSection').then(m => ({ default: m.BlogSection })));
const BookShowcase = lazy(() => import('./BookShowcase').then(m => ({ default: m.BookShowcase })));

// Import local hero image (optimized WebP, ~81KB vs external Unsplash)
import heroImage from '../assets/hero-app-preview.webp';

interface PricingCardProps {
    title: string;
    price: string;
    capacityLabel: string;
    features: string[];
    featured?: boolean;
    onSubscribe: () => void;
    t: any;
}

const PricingCard: React.FC<PricingCardProps> = ({ title, price, capacityLabel, features, featured, onSubscribe, t }) => (
    <article className={`glass-card p-10 transition-all duration-500 hover:-translate-y-4 ${featured ? 'border-indigo-500/30 shadow-[0_0_50px_rgba(79,70,229,0.15)]' : 'border-white/5'}`}>
        {featured && (
            <div className="mb-6">
                <span className="bg-white/10 text-white text-xs font-bold px-4 py-2 rounded-full uppercase tracking-widest border border-white/10">
                    {t('landing.pricing.recommended')}
                </span>
            </div>
        )}
        <h3 className="text-2xl font-bold text-white mb-2">{title}</h3>
        <div className="flex items-baseline gap-1 mb-8">
            <span className="text-5xl font-bold tracking-tighter text-white">{getCurrencySymbol()}{price}</span>
            {parseInt(price) > 0 && <span className="text-slate-500 font-medium ml-2">{t('landing.pricing.month_suffix')}</span>}
        </div>

        <div className="space-y-4 mb-10">
            <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-indigo-500/10 flex items-center justify-center">
                    <BookOpen className="w-5 h-5 text-indigo-400" />
                </div>
                <div>
                    <p className="text-xs font-bold text-indigo-400 uppercase tracking-wider">{t('landing.pricing.capacity')}</p>
                    <p className="text-lg font-bold text-slate-200">{capacityLabel}</p>
                </div>
            </div>
        </div>

        <ul className="space-y-4 mb-10">
            {features.map((feature, i) => (
                <li key={i} className="flex items-start gap-3 text-sm text-slate-400">
                    <Check className="w-5 h-5 text-indigo-500 shrink-0" />
                    <span>{feature}</span>
                </li>
            ))}
        </ul>
        <button
            onClick={onSubscribe}
            className={`w-full py-4 px-6 rounded-full font-bold text-sm tracking-wide transition-all ${featured
                ? 'btn-primary'
                : 'btn-secondary'
                }`}
        >
            {parseInt(price) === 0 ? t('landing.pricing.start_free') : t('landing.pricing.choose_plan')}
        </button>
    </article>
);

interface LandingPageProps {
    onStartTrial: () => void;
    onStartGuest: () => void;
    onLogin: () => void;
    onOpenBlog: (slug: string) => void;
}

export const LandingPage: React.FC<LandingPageProps> = ({ onStartTrial, onStartGuest, onLogin, onOpenBlog }) => {
    const { t, i18n } = useTranslation();
    const [isDisclaimerOpen, setIsDisclaimerOpen] = useState(false);
    const [isContactOpen, setIsContactOpen] = useState(false);
    const [openFaq, setOpenFaq] = useState<number | null>(null);

    useEffect(() => {
        document.documentElement.lang = i18n.language?.substring(0, 2) || 'es';
        document.title = t('seo.title');

        // Dynamic Meta Tags for SEO
        const metaDesc = document.querySelector('meta[name="description"]');
        if (metaDesc) metaDesc.setAttribute('content', t('seo.description'));

        const metaKeywords = document.querySelector('meta[name="keywords"]');
        if (metaKeywords) metaKeywords.setAttribute('content', t('seo.keywords'));

        // NOTE: canonical and hreflang are set in static HTML (index.html).
        // In a SPA, all languages serve from the same URL, so canonical
        // and all hreflang entries point to https://yournovel.app/.
        // Do NOT modify them dynamically — it causes Lighthouse conflicts.

        // Dynamic JSON-LD FAQPage (language-aware)
        const faqKeys = ['q1','q2','q3','q4','q5','q6','q7','q8'];
        const faqJsonLd = {
            '@context': 'https://schema.org',
            '@type': 'FAQPage',
            'mainEntity': faqKeys.map(qKey => ({
                '@type': 'Question',
                'name': t(`landing.faq.${qKey}`),
                'acceptedAnswer': {
                    '@type': 'Answer',
                    'text': t(`landing.faq.${qKey.replace('q','a')}`)
                }
            }))
        };
        let faqScript = document.getElementById('dynamic-faq-jsonld') as HTMLScriptElement | null;
        if (!faqScript) {
            faqScript = document.createElement('script');
            faqScript.id = 'dynamic-faq-jsonld';
            faqScript.type = 'application/ld+json';
            document.head.appendChild(faqScript);
        }
        faqScript.textContent = JSON.stringify(faqJsonLd);

    }, [i18n.language, t]);

    // ===== MOBILE APP: Simplified Welcome Screen (no SEO needed) =====
    if (Capacitor.isNativePlatform()) {
        return (
            <div className="min-h-screen relative overflow-hidden flex flex-col items-center justify-center px-6">
                {/* Background glow */}
                <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                    <div className="hero-glow-blob bg-indigo-600 w-[80vw] h-[80vw] top-[-30%] left-[-20%] opacity-20" />
                    <div className="hero-glow-blob bg-fuchsia-600 w-[60vw] h-[60vw] bottom-[-20%] right-[-15%] opacity-15" />
                </div>

                <div className="relative z-10 text-center max-w-md mx-auto">
                    {/* Logo */}
                    <div className="flex items-center justify-center gap-3 mb-8">
                        <div className="relative">
                            <div className="absolute inset-0 bg-indigo-500 blur-lg opacity-50"></div>
                            <Feather className="w-12 h-12 text-white relative z-10" />
                        </div>
                    </div>

                    <h1 className="text-5xl font-bold tracking-tighter text-white mb-4 leading-tight">
                        YourNovel
                    </h1>
                    <p className="text-lg text-slate-400 mb-12 leading-relaxed">
                        {t('landing.hero.description', '').replace(/<[^>]*>/g, '')}
                    </p>

                    <div className="flex flex-col gap-4 w-full">
                        <button
                            onClick={onStartGuest}
                            className="btn-primary text-lg px-8 py-4 flex items-center justify-center gap-3 w-full"
                        >
                            <Feather className="w-5 h-5" />
                            <span>{t('landing.hero.cta_start_guest', 'Empieza a escribir GRATIS')}</span>
                        </button>

                        <button
                            onClick={() => onLogin()}
                            className="btn-secondary text-lg px-8 py-4 w-full"
                        >
                            {t('landing.nav.login', 'Iniciar sesión')}
                        </button>

                        <button
                            onClick={onStartTrial}
                            className="text-sm font-medium text-slate-500 hover:text-white transition-colors mt-4"
                        >
                            {t('landing.nav.create_account', 'Crear cuenta')}
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    // ===== WEB: Full landing page with SEO =====

    return (
        <main className="min-h-screen relative overflow-hidden selection:bg-indigo-500/30 selection:text-white">

            {/* Ambient Background - Antigravity Globs */}
            <div className="fixed inset-0 z-0 overflow-hidden pointer-events-none">
                <div className="hero-glow-blob bg-indigo-600 w-[60vw] h-[60vw] top-[-20%] left-[-10%] animate-spin-slow opacity-20" style={{ animationDuration: '40s' }} />
                <div className="hero-glow-blob bg-fuchsia-600 w-[50vw] h-[50vw] bottom-[-10%] right-[-10%] animate-gravity opacity-15" />
            </div>

            {/* Navigation */}
            <nav className="fixed top-0 left-0 right-0 z-50 glass-nav transition-all duration-300">
                <div className="max-w-[1400px] mx-auto px-6 h-20 flex justify-between items-center">
                    <div className="flex items-center gap-4 cursor-pointer" onClick={() => window.scrollTo(0, 0)}>
                        <div className="relative">
                            <div className="absolute inset-0 bg-indigo-500 blur-lg opacity-40"></div>
                            <BookOpen className="w-8 h-8 text-white relative z-10" />
                        </div>
                        <span className="font-bold text-white text-xl tracking-tight hidden sm:block">YourNovel</span>
                    </div>

                    <div className="flex items-center gap-6">
                        <button onClick={onLogin} className="text-sm font-medium text-slate-300 hover:text-white transition-colors">
                            {t('landing.nav.login')}
                        </button>
                        <button onClick={onStartTrial} className="btn-primary text-sm px-6 py-2.5">
                            {t('landing.nav.create_account')}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Hero Section */}
            <section className="relative z-10 pt-48 pb-32 px-6">
                <div className="max-w-[1400px] mx-auto text-center">


                    <h1 className="text-7xl md:text-9xl font-bold tracking-tighter text-white mb-10 leading-[0.9] animate-fade-in">
                        <span className="block">{t('landing.hero.title_prefix')}</span>
                        <span className="text-antigravity block mt-2 pb-4">{t('landing.hero.title_highlight')}</span>
                    </h1>

                    <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mx-auto leading-relaxed mb-16 font-light animate-fade-in delay-100">
                        <Trans i18nKey="landing.hero.description" components={{ strong: <span className="text-indigo-300 font-medium" /> }} />
                    </p>

                    <div className="flex flex-col sm:flex-row justify-center gap-6 animate-fade-in delay-200">
                        <button onClick={onStartGuest} className="btn-primary text-lg px-10 py-4 flex items-center justify-center gap-3">
                            <span>{t('landing.hero.cta_start_guest', 'Empieza a escribir tu libro GRATIS')}</span>
                            <ArrowRight className="w-5 h-5" />
                        </button>
                        <button onClick={onStartTrial} className="btn-secondary text-lg px-8 py-4 flex items-center justify-center gap-2">
                            <span>{t('landing.hero.cta_plans', 'Ver planes')}</span>
                        </button>
                    </div>

                    {/* Trust indicators */}
                    <div className="mt-8 flex flex-col sm:flex-row items-center justify-center gap-4 sm:gap-8 animate-fade-in delay-300">
                        <span className="text-sm text-slate-400 font-medium">{t('landing.hero.trust_rating')}</span>
                        <span className="hidden sm:block w-px h-4 bg-white/10"></span>
                        <span className="text-sm text-slate-500">{t('landing.hero.trust_authors')}</span>
                    </div>

                    {/* Floating UI Elements Demo */}
                    <div className="mt-32 relative max-w-5xl mx-auto">
                        <div className="glass-card p-1 border-white/10 rounded-2xl animate-gravity" style={{ animationDuration: '6s', transform: 'perspective(1000px) rotateX(10deg)' }}>
                            <img
                                src={heroImage}
                                alt="YourNovel.app AI Book Writing Interface"
                                width="1200"
                                height="675"
                                fetchPriority="high"
                                className="rounded-xl opacity-80 w-full h-auto"
                            />
                            <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-60"></div>
                            <div className="absolute bottom-10 left-10 text-left">
                                <div className="text-xl font-bold text-white mb-2">{t('landing.hero.demo_title')}</div>
                                <div className="text-slate-400">{t('landing.hero.demo_subtitle')}</div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* Features Staggered */}
            <section className="py-32 px-6 relative z-10">
                <div className="max-w-[1400px] mx-auto space-y-32">
                    {[
                        { icon: Zap, color: 'text-amber-400', title: 'context_title', desc: 'context_desc', align: 'left' },
                        { icon: BarChart, color: 'text-pink-400', title: 'volume_title', desc: 'volume_desc', align: 'right' },
                        { icon: ShieldCheck, color: 'text-emerald-400', title: 'control_title', desc: 'control_desc', align: 'left' }
                    ].map((feature, idx) => (
                        <div key={idx} className={`flex flex-col md:flex-row items-center gap-16 ${feature.align === 'right' ? 'md:flex-row-reverse' : ''}`}>
                            <div className="flex-1 space-y-8">
                                <div className="w-16 h-16 rounded-2xl bg-white/5 border border-white/10 flex items-center justify-center">
                                    <feature.icon className={`w-8 h-8 ${feature.color}`} />
                                </div>
                                <h2 className="text-4xl md:text-5xl font-bold text-white tracking-tight">{t(`landing.features.${feature.title}`)}</h2>
                                <p className="text-xl text-slate-400 leading-relaxed">
                                    <Trans i18nKey={`landing.features.${feature.desc}`} components={{ strong: <span className="text-white font-medium" /> }} />
                                </p>
                            </div>
                            <div className="flex-1 w-full">
                                <div className="aspect-square rounded-3xl border border-white/10 relative overflow-hidden group">
                                    <img
                                        src={`${import.meta.env.BASE_URL}assets/${idx === 0 ? 'feature-infinite-context.webp' : idx === 1 ? 'feature-massive-volume.webp' : 'feature-total-control.webp'}`}
                                        alt={t(`landing.features.${feature.title}`)}
                                        width="600"
                                        height="600"
                                        loading="lazy"
                                        decoding="async"
                                        className="w-full h-full object-cover rounded-3xl opacity-80 group-hover:opacity-100 group-hover:scale-105 transition-all duration-500"
                                    />
                                    <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent rounded-3xl"></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Testimonials Section */}
            <section className="py-32 px-6 relative z-10">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">{t('landing.testimonials.title')}</h2>
                        <p className="text-xl text-slate-400 max-w-2xl mx-auto">{t('landing.testimonials.subtitle')}</p>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                        {(t('landing.testimonials.items', { returnObjects: true }) as any[]).map((item: any, idx: number) => (
                            <div
                                key={idx}
                                className="glass-card p-8 rounded-2xl border border-white/10 hover:border-indigo-500/30 transition-all duration-300 hover:-translate-y-1 group"
                            >
                                {/* Stars */}
                                <div className="flex gap-1 mb-4">
                                    {[...Array(5)].map((_, i) => (
                                        <svg key={i} className="w-4 h-4 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                                            <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                                        </svg>
                                    ))}
                                </div>

                                {/* Quote */}
                                <p className="text-slate-300 leading-relaxed mb-6 text-sm italic">"{item.quote}"</p>

                                {/* Author */}
                                <div className="flex items-center gap-3">
                                    <div className="w-10 h-10 rounded-full bg-gradient-to-br from-indigo-500 to-fuchsia-500 flex items-center justify-center text-white font-bold text-sm">
                                        {item.name.split(' ').map((n: string) => n[0]).join('')}
                                    </div>
                                    <div>
                                        <div className="text-white font-semibold text-sm">{item.name}</div>
                                        <div className="text-slate-500 text-xs">{item.role}</div>
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>



            {/* Book Showcase — Real book made with YourNovel published on Amazon */}
            <Suspense fallback={<div className="py-32" />}>
                <BookShowcase onStartGuest={onStartGuest} />
            </Suspense>

            {/* Blog Section */}
            <Suspense fallback={<div className="py-32" />}>
                <BlogSection onOpenPost={onOpenBlog} />
            </Suspense>

            {/* Pricing Section */}
            <section className="py-32 px-6 relative z-10" id="pricing">
                <div className="max-w-[1400px] mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">{t('landing.pricing.title')}</h2>
                        <p className="text-xl text-slate-400 max-w-2xl mx-auto">{t('landing.pricing.subtitle')}</p>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
                        <PricingCard
                            title={t('landing.pricing.plans.free.title')}
                            price="0"
                            capacityLabel={t('landing.pricing.free_capacity')}
                            onSubscribe={onStartGuest}
                            t={t}
                            features={t('landing.pricing.plans.free.features', { returnObjects: true }) as string[]}
                        />
                        <PricingCard
                            title={t('landing.pricing.plans.starter.title')}
                            price="19"
                            capacityLabel={`1 ${t('landing.pricing.books')}`}
                            onSubscribe={onStartTrial}
                            t={t}
                            features={t('landing.pricing.plans.starter.features', { returnObjects: true }) as string[]}
                        />
                        <PricingCard
                            featured
                            title={t('landing.pricing.plans.pro.title')}
                            price="39"
                            capacityLabel={`3 ${t('landing.pricing.books')}`}
                            onSubscribe={onStartTrial}
                            t={t}
                            features={t('landing.pricing.plans.pro.features', { returnObjects: true }) as string[]}
                        />
                        <PricingCard
                            title={t('landing.pricing.plans.agency.title')}
                            price="89"
                            capacityLabel={`10 ${t('landing.pricing.books')}`}
                            onSubscribe={onStartTrial}
                            t={t}
                            features={t('landing.pricing.plans.agency.features', { returnObjects: true }) as string[]}
                        />
                    </div>
                </div>
            </section>

            {/* FAQ Section */}
            <section className="py-32 px-6 relative z-10" id="faq">
                <div className="max-w-[900px] mx-auto">
                    <div className="text-center mb-20">
                        <h2 className="text-5xl md:text-6xl font-bold text-white mb-6 tracking-tight">{t('landing.faq.title')}</h2>
                        <p className="text-xl text-slate-400 max-w-2xl mx-auto">{t('landing.faq.subtitle')}</p>
                    </div>

                    <div className="space-y-4">
                        {['q1','q2','q3','q4','q5','q6','q7','q8'].map((qKey, idx) => (
                            <div
                                key={qKey}
                                className="glass-card border border-white/10 rounded-2xl overflow-hidden transition-all duration-300 hover:border-indigo-500/30"
                            >
                                <button
                                    onClick={() => setOpenFaq(openFaq === idx ? null : idx)}
                                    className="w-full flex items-center justify-between p-6 text-left group"
                                    aria-expanded={openFaq === idx}
                                >
                                    <span className="text-lg font-semibold text-white pr-4 group-hover:text-indigo-300 transition-colors">
                                        {t(`landing.faq.${qKey}`)}
                                    </span>
                                    <ChevronDown
                                        className={`w-5 h-5 text-slate-400 shrink-0 transition-transform duration-300 ${
                                            openFaq === idx ? 'rotate-180 text-indigo-400' : ''
                                        }`}
                                    />
                                </button>
                                <div
                                    className={`overflow-hidden transition-all duration-300 ease-in-out ${
                                        openFaq === idx ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
                                    }`}
                                >
                                    <div className="px-6 pb-6 text-slate-400 leading-relaxed border-t border-white/5 pt-4">
                                        {t(`landing.faq.${qKey.replace('q','a')}`)}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className="py-12 border-t border-white/5 relative z-10 bg-black">
                <div className="max-w-[1400px] mx-auto px-6 flex flex-col md:flex-row justify-between items-center gap-6">
                    <div className="flex items-center gap-2">
                        <BookOpen className="w-5 h-5 text-indigo-500" />
                        <span className="font-bold text-slate-300">YourNovel</span>
                    </div>

                    <div className="flex gap-8 text-sm text-slate-400 font-medium">
                        {['es', 'en', 'fr', 'de', 'it', 'pt'].map(lang => (
                            <a
                                key={lang}
                                href={`?lang=${lang}`} // Crawlable links
                                onClick={(e) => {
                                    e.preventDefault();
                                    const newUrl = `${window.location.pathname}?lang=${lang}`;
                                    window.history.pushState({}, '', newUrl);
                                    i18n.changeLanguage(lang);
                                }}
                                className={`hover:text-white transition-colors uppercase ${i18n.language === lang ? 'text-indigo-400' : ''}`}
                            >
                                {lang}
                            </a>
                        ))}
                    </div>

                    <div className="text-slate-400 text-sm flex flex-col items-end gap-2">
                        <button
                            onClick={() => setIsContactOpen(true)}
                            className="text-slate-400 hover:text-white transition-colors font-medium mb-1"
                        >
                            {t('contact.landing_cta', '¿Tienes dudas? Contáctanos')}
                        </button>
                        <span>{t('landing.footer')}</span>
                        <button
                            onClick={() => setIsDisclaimerOpen(true)}
                            className="text-slate-400 hover:text-indigo-400 text-xs transition-colors underline decoration-slate-600 hover:decoration-indigo-500/50"
                        >
                            {t('disclaimer.link')}
                        </button>
                    </div>
                </div>
            </footer>

            {isDisclaimerOpen && (
                <Suspense fallback={null}>
                    <DisclaimerModal isOpen={isDisclaimerOpen} onClose={() => setIsDisclaimerOpen(false)} />
                </Suspense>
            )}
            {isContactOpen && (
                <Suspense fallback={null}>
                    <ContactModal
                        isOpen={isContactOpen}
                        onClose={() => setIsContactOpen(false)}
                        page="landing"
                    />
                </Suspense>
            )}

        </main >
    );
};
